import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { EQUIPMENT_STRIP } from "@/lib/lime-data";

const SCROLL_SPEED_PX_PER_SEC = 75;
const ARROW_NUDGE_PX = 300;
const NUDGE_TRANSITION_MS = 350;
const NUDGE_CLEAR_DELAY_MS = 380;

export default function EquipmentStrip() {
  // duplicate for seamless loop
  const items = [...EQUIPMENT_STRIP, ...EQUIPMENT_STRIP];
  const trackRef = useRef(null);
  const offsetRef = useRef(0);
  const halfRef = useRef(0);
  const [paused, setPaused] = useState(false);

  // Measure half-width of the duplicated track (used for seamless loop wrap).
  useLayoutEffect(() => {
    const measure = () => {
      const track = trackRef.current;
      if (track) halfRef.current = track.scrollWidth / 2;
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  // GPU-accelerated transform loop (matches the section-2 marquee feel).
  useEffect(() => {
    let raf;
    let last = performance.now();
    const speed = SCROLL_SPEED_PX_PER_SEC;
    const tick = (now) => {
      const dt = (now - last) / 1000;
      last = now;
      if (!paused) {
        offsetRef.current += speed * dt;
        if (halfRef.current && offsetRef.current >= halfRef.current) {
          offsetRef.current -= halfRef.current;
        }
        const track = trackRef.current;
        if (track) {
          track.style.transform = `translate3d(${-offsetRef.current}px, 0, 0)`;
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [paused]);

  const nudge = (dir) => {
    if (!halfRef.current) return;
    offsetRef.current += dir * ARROW_NUDGE_PX;
    // wrap in both directions
    if (offsetRef.current < 0) offsetRef.current += halfRef.current;
    if (offsetRef.current >= halfRef.current) offsetRef.current -= halfRef.current;
    const track = trackRef.current;
    if (track) {
      // smooth nudge using a one-shot transition
      track.style.transition = `transform ${NUDGE_TRANSITION_MS}ms ease-out`;
      track.style.transform = `translate3d(${-offsetRef.current}px, 0, 0)`;
      setTimeout(() => {
        if (track) track.style.transition = "";
      }, NUDGE_CLEAR_DELAY_MS);
    }
  };

  return (
    <section
      id="gears"
      className="py-16 border-t border-[#262626] bg-[#0a0a0a] overflow-hidden"
      data-testid="equipment-strip"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-6 flex items-center justify-between flex-wrap gap-3">
        <div className="font-mono-overline text-[#E4FF00]">Gear · Pro-grade only</div>
        <div className="text-xs font-mono uppercase tracking-widest text-[#666]">
          Hover to pause · Use arrows to scroll
        </div>
      </div>
      <div
        className="relative"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        {/* Left arrow */}
        <button
          type="button"
          aria-label="Scroll gear left"
          data-testid="gear-arrow-left"
          onClick={() => nudge(-1)}
          className="hidden sm:flex absolute left-3 top-1/2 -translate-y-1/2 z-10 w-10 h-10 items-center justify-center bg-black/70 border border-[#262626] text-[#f4f4f0] hover:bg-[#E4FF00] hover:text-black hover:border-[#E4FF00] backdrop-blur transition-colors"
        >
          <ChevronLeft size={20} />
        </button>

        {/* Right arrow */}
        <button
          type="button"
          aria-label="Scroll gear right"
          data-testid="gear-arrow-right"
          onClick={() => nudge(1)}
          className="hidden sm:flex absolute right-3 top-1/2 -translate-y-1/2 z-10 w-10 h-10 items-center justify-center bg-black/70 border border-[#262626] text-[#f4f4f0] hover:bg-[#E4FF00] hover:text-black hover:border-[#E4FF00] backdrop-blur transition-colors"
        >
          <ChevronRight size={20} />
        </button>

        <div className="overflow-hidden">
          <div
            ref={trackRef}
            className="flex gap-4 will-change-transform"
            style={{ transform: "translate3d(0,0,0)" }}
          >
            {items.map((it, i) => (
              <div
                key={`${it.label}-${i}`}
                className="relative shrink-0 w-[280px] h-[180px] border border-[#262626] overflow-hidden hover:border-[#E4FF00]/60 transition-colors"
              >
                {it.img ? (
                  <>
                    <img
                      src={it.img}
                      alt={it.label}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                  </>
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-[#141414] via-[#0f0f0f] to-[#1a1a1a]">
                    <div
                      className="absolute inset-0 opacity-20"
                      style={{
                        backgroundImage:
                          "radial-gradient(circle at 20% 30%, #E4FF00 0%, transparent 50%)",
                      }}
                    />
                    <div className="absolute top-4 right-4 font-mono text-[10px] uppercase tracking-widest text-[#444]">
                      Img · pending
                    </div>
                  </div>
                )}
                <div className="absolute left-4 bottom-4 right-4">
                  <div className="font-display font-bold text-[#f4f4f0] leading-tight">
                    {it.label}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
